import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuthStore'
import { PageHeader } from '../components/PageHeader'
import { DashboardCard } from '../components/DashboardCard'
import apiService from '../services/api'

// Local formatters
const formatThaiCurrency = (amount: number) => {
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: 'THB',
    minimumFractionDigits: 0
  }).format(amount)
}

interface Course {
  id: number
  name: string
  description: string
  price: number
  schedule: string
  subjects: string[]
}

interface EnrollmentFormData {
  student_name: string
  nickname: string
  student_prefix: 'ด.ช.' | 'ด.ญ.'
}

export const EnrollmentFormPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  
  const course = location.state?.course as Course
  const [formData, setFormData] = useState<EnrollmentFormData>({
    student_name: '',
    nickname: '',
    student_prefix: 'ด.ช.'
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Get current month for enrollment
  const currentDate = new Date()
  const enrollmentMonth = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}`

  useEffect(() => {
    // Redirect if no course data
    if (!course) {
      navigate('/courses')
      return
    }
  }, [course, navigate])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.student_name.trim()) {
      newErrors.student_name = 'กรุณากรอกชื่อนักเรียน'
    } else if (formData.student_name.trim().length < 2) {
      newErrors.student_name = 'ชื่อนักเรียนต้องมีอย่างน้อย 2 ตัวอักษร'
    }

    if (!formData.nickname.trim()) {
      newErrors.nickname = 'กรุณากรอกชื่อเล่น'
    } else if (formData.nickname.trim().length < 1) {
      newErrors.nickname = 'ชื่อเล่นต้องมีอย่างน้อย 1 ตัวอักษร'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm() || !user?.id || !course) {
      return
    }

    try {
      setLoading(true)
      
      const enrollmentData = {
        parent_id: user.id,
        student_name: `${formData.student_prefix}${formData.student_name.trim()}`,
        nickname: formData.nickname.trim(),
        course_id: course.id,
        month_year: enrollmentMonth
      }

      const enrollment = await apiService.createEnrollment(enrollmentData)
      
      // Navigate to payment instructions
      navigate('/enrollment/payment', {
        state: {
          enrollment,
          course,
          from: 'enrollment-form'
        }
      })

    } catch (error) {
      console.error('Enrollment failed:', error)
      setErrors({ submit: 'การลงทะเบียนล้มเหลว กรุณาลองใหม่อีกครั้ง' })
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: keyof EnrollmentFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  if (!course) {
    return null // Will redirect via useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="ลงทะเบียนเรียน" 
        showBackButton
        onBack={() => navigate('/courses')}
      />
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Course Summary */}
        <DashboardCard title="คอร์สที่เลือก" className="mb-6">
          <div className="space-y-3">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-gray-900">{course.name}</h3>
                <p className="text-sm text-gray-600 mt-1">{course.schedule}</p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-blue-600">
                  {formatThaiCurrency(course.price)}
                </div>
                <div className="text-sm text-gray-500">ต่อเดือน</div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-1">
              {course.subjects.map((subject, index) => (
                <span
                  key={index}
                  className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                >
                  {subject}
                </span>
              ))}
            </div>
          </div>
        </DashboardCard>

        {/* Enrollment Form */}
        <DashboardCard title="ข้อมูลนักเรียน">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Student Prefix */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                คำนำหน้า
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="prefix"
                    value="ด.ช."
                    checked={formData.student_prefix === 'ด.ช.'}
                    onChange={(e) => handleInputChange('student_prefix', e.target.value as 'ด.ช.' | 'ด.ญ.')}
                    className="mr-2"
                  />
                  <span className="text-sm">ด.ช. (เด็กชาย)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="prefix"
                    value="ด.ญ."
                    checked={formData.student_prefix === 'ด.ญ.'}
                    onChange={(e) => handleInputChange('student_prefix', e.target.value as 'ด.ช.' | 'ด.ญ.')}
                    className="mr-2"
                  />
                  <span className="text-sm">ด.ญ. (เด็กหญิง)</span>
                </label>
              </div>
            </div>

            {/* Student Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ชื่อ-นามสกุล นักเรียน <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.student_name}
                onChange={(e) => handleInputChange('student_name', e.target.value)}
                placeholder="กรอกชื่อ-นามสกุล เช่น สมชาย ใจดี"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.student_name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.student_name && (
                <p className="mt-1 text-sm text-red-600">{errors.student_name}</p>
              )}
              <div className="mt-1 text-xs text-gray-500">
                ชื่อเต็มจะแสดงเป็น: {formData.student_prefix}{formData.student_name || 'ชื่อ-นามสกุล'}
              </div>
            </div>

            {/* Nickname */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ชื่อเล่น <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.nickname}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
                placeholder="กรอกชื่อเล่น เช่น ชาย"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.nickname ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.nickname && (
                <p className="mt-1 text-sm text-red-600">{errors.nickname}</p>
              )}
            </div>

            {/* Enrollment Month */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                เดือนที่ลงทะเบียน
              </label>
              <input
                type="text"
                value={enrollmentMonth}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
              />
              <div className="mt-1 text-xs text-gray-500">
                ลงทะเบียนสำหรับเดือนปัจจุบัน
              </div>
            </div>

            {/* Error Message */}
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex space-x-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/courses')}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                กลับไปเลือกคอร์ส
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 py-2 px-4 rounded-lg font-medium text-white transition-colors ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? 'กำลังลงทะเบียน...' : 'ลงทะเบียนเรียน'}
              </button>
            </div>
          </form>
        </DashboardCard>

        {/* Additional Info */}
        <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
          <h4 className="font-medium text-yellow-900 mb-2">ข้อมูลสำคัญ</h4>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>• กรอกข้อมูลให้ครบถ้วนและตรวจสอบความถูกต้องก่อนลงทะเบียน</li>
            <li>• หลังจากลงทะเบียนจะต้องชำระเงินภายใน 24 ชั่วโมง</li>
            <li>• การลงทะเบียนจะสมบูรณ์เมื่อเจ้าหน้าที่ตรวจสอบการชำระเงินแล้ว</li>
          </ul>
        </div>
      </div>
    </div>
  )
}